import CreateDocumentModal from '@/components/knowledge/CreateDocumentModal';
import { documentStoreService } from '@/services/document-store.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  Clock,
  Database,
  FolderOpen,
  Plus,
  Search,
  Activity,
  AlertTriangle,
  Trash,
  Edit,
  CheckCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { DOCUMENT_STORE_PERMISSION } from '@/constants/constant-permissions';
import StatusIndicator from '@/components/knowledge/StatusIndicator';
import { useRealTimeStoresList } from '@/hooks/useRealTimeStatus';
import UpdateDocumentModal from '@/components/knowledge/UpdateDocumentModal';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import TooltipArrow from '@/components/ui/TooltipArrow';
import Pagination from '@/components/ui/Pagination';

// UI-specific interface extending the API DocumentStore
interface DocumentStoreUI {
  id: string;
  name: string;
  description: string;
  documentsCount: number;
  aiProcessedCount: number;
  totalSize: string;
  createdAt: string;
  lastActivity: string;
  status: string;
}

export default function Documents() {
  usePermissionRouteAccess([
    DOCUMENT_STORE_PERMISSION.ADMIN_DOCUMENT_STORE_READ,
    DOCUMENT_STORE_PERMISSION.ADMIN_DOCUMENT_STORE_WRITE,
  ]);
  const canWrite = useHasPermission([DOCUMENT_STORE_PERMISSION.ADMIN_DOCUMENT_STORE_WRITE]);
  const navigate = useNavigate();
  const [showCreateStoreModal, setShowCreateStoreModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState<DocumentStoreUI | null>(
    null,
  );
  const [storeToDelete, setStoreToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const skip = (currentPage - 1) * itemsPerPage;

  const {
    stores: allStores,
    totalRecords,
    isLoading: isLoadingStores,
  } = useRealTimeStoresList(
    true, // enabled
    10000, // poll every 10 seconds
    skip,
    itemsPerPage,
    searchQuery,
  );

  // Fetch metrics using dedicated lightweight API
  const { data: metricsData } = useQuery({
    queryKey: ['document-store-metrics'],
    queryFn: () => documentStoreService.getMetrics(),
    refetchInterval: 10000, // Refresh every 10 seconds
    staleTime: 5000,
  });

  const overallStats = metricsData || {
    totalStores: 0,
    storesWithProcessing: 0,
    totalDocuments: 0,
    processingDocuments: 0,
    completedDocuments: 0,
    failedDocuments: 0,
  };

  // Pagination calculations (server-side)
  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  // Reset to page 1 when search query changes
  useMemo(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const refetchStores = () => {
    queryClient.invalidateQueries({ queryKey: ['document-stores-list'] });
    queryClient.invalidateQueries({ queryKey: ['document-store-metrics'] });
  };

  const createStoreMutation = useMutation({
    mutationFn: documentStoreService.createDocumentStore,
    onSuccess: () => {
      toast.success('Document store created successfully!');
      refetchStores();
      setShowCreateStoreModal(false);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.error_description ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to create document store.',
      );
    },
  });
  const updateStoreMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      documentStoreService.updateDocumentStore(id, data),
    onSuccess: () => {
      toast.success('Document store updated successfully!');
      refetchStores();
      setShowUpdateModal(false); // close modal
      setSelectedStore(null);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.error_description ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to update document store.',
      );
    },
  });
  const stores: DocumentStoreUI[] = useMemo(() => {
    return allStores.map((d) => ({
      id: d.id,
      name: d.name,
      description: d.description || 'N/A',
      documentsCount: d.document_urls.length,
      aiProcessedCount: d.document_urls.filter((doc) => doc.status === 'DONE')
        .length,
      totalSize: ((totalBytes) => {
        if (totalBytes <= 0) return 'N/A';

        // Show KB for < 1MB, MB for < 1GB, GB for >= 1GB
        if (totalBytes < 1024 * 1024) {
          return `${(totalBytes / 1024).toFixed(2)} KB`;
        } else if (totalBytes < 1024 * 1024 * 1024) {
          return `${(totalBytes / (1024 * 1024)).toFixed(2)} MB`;
        } else {
          return `${(totalBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
        }
      })(
        d.document_urls.reduce((total, doc) => {
          const size = Number(doc.fileSize);
          return isNaN(size) ? total : total + size;
        }, 0),
      ),
      createdAt: format(new Date(d.createdAt), 'PP'),
      lastActivity: format(new Date(d.updatedAt), 'PPpp'),
      status: d.status,
    }));
  }, [allStores]);

  const deleteStoreMutation = useMutation({
    mutationFn: documentStoreService.deleteDocumentStore,
    onSuccess: () => {
      toast.success('Document store deleted successfully!');
      refetchStores();
      setShowDeleteModal(false);
      setSelectedStore(null);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete document store.');
      setLoading(false);
    },
  });

  const handleDeleteStore = () => {
    if (!selectedStore) return;
    setLoading(true);
    deleteStoreMutation.mutate(selectedStore.id, {
      onSettled: () => setLoading(false),
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-indigo-600 to-brand-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Database className="w-7 h-7" />
              Document Store
            </h1>
            <p className="text-brand-indigo-100">
              Manage document repositories with AI-powered processing and
              content extraction
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => canWrite && setShowCreateStoreModal(true)}
              disabled={!canWrite}
              title={!canWrite ? 'You do not have permission' : undefined}
              className={`flex items-center gap-2 px-4 py-2 bg-white text-brand-indigo-600 rounded-lg hover:bg-brand-indigo-50 transition-colors${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">New Store</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-6">
          {/* Total Stores */}
          <div className="bg-white/15 backdrop-blur-md rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-all">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-white/20 rounded-lg">
                <Database className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-3xl font-bold text-white mb-1">
              {overallStats.totalStores}
            </p>
            <p className="text-brand-indigo-100 text-sm font-medium">Total Stores</p>
          </div>

          {/* Total Documents */}
          <div className="bg-white/15 backdrop-blur-md rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-all">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-white/20 rounded-lg">
                <FolderOpen className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-3xl font-bold text-white mb-1">
              {overallStats.totalDocuments}
            </p>
            <p className="text-brand-indigo-100 text-sm font-medium">
              Total Documents
            </p>
          </div>

          {/* AI Processed */}
          <div className="bg-white/15 backdrop-blur-md rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-all">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-white/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              {overallStats.totalDocuments > 0 && (
                <span className="text-xs font-semibold text-white bg-white/20 px-2 py-1 rounded-full">
                  {Math.round(
                    (overallStats.completedDocuments /
                      overallStats.totalDocuments) *
                      100,
                  )}
                  %
                </span>
              )}
            </div>
            <p className="text-3xl font-bold text-white mb-1">
              {overallStats.completedDocuments}
            </p>
            <p className="text-brand-indigo-100 text-sm font-medium">AI Processed</p>
          </div>

          {/* Processing */}
          <div className="bg-white/15 backdrop-blur-md rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-all">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-white/20 rounded-lg">
                <Activity className="w-5 h-5 text-white" />
              </div>
              {overallStats.processingDocuments > 0 && (
                <div className="flex items-center gap-1">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-300 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-200"></span>
                  </span>
                </div>
              )}
            </div>
            <p className="text-3xl font-bold text-white mb-1">
              {overallStats.processingDocuments}
            </p>
            <p className="text-brand-indigo-100 text-sm font-medium">Processing</p>
          </div>

          {/* Failed */}
          <div className="bg-white/15 backdrop-blur-md rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-all">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-white/20 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              {overallStats.failedDocuments > 0 && (
                <span className="text-xs font-semibold text-white bg-white/20 px-2 py-1 rounded-full">
                  Alert
                </span>
              )}
            </div>
            <p className="text-3xl font-bold text-white mb-1">
              {overallStats.failedDocuments}
            </p>
            <p className="text-brand-indigo-100 text-sm font-medium">Failed</p>
          </div>
        </div>

        {/* Overall Progress */}
        {/* {overallStats.totalDocuments > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-brand-indigo-100 text-sm">Overall Processing Progress</p>
              <LiveStatusIndicator
                isPolling={isPolling}
                className="text-brand-indigo-100"
              />
            </div>
            <ProcessingProgress
              total={overallStats.totalDocuments}
              completed={overallStats.completedDocuments}
              processing={overallStats.processingDocuments}
              failed={overallStats.failedDocuments}
              showDetails={false}
            />
          </div>
        )} */}
      </div>

      {/* Search Bar */}
      <div className="card">
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search stores..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-indigo-500 focus:border-brand-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {isLoadingStores
          ? Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="card">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3 flex-grow min-w-0 overflow-hidden">
                    <SkeletonLoader
                      type="custom"
                      className="w-12 h-12 rounded-lg flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <SkeletonLoader type="title" className="h-5 w-40 mb-2" />
                      <SkeletonLoader type="text" className="h-4 w-full" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <SkeletonLoader
                      type="custom"
                      className="w-20 h-6 rounded-full"
                    />
                    <SkeletonLoader type="custom" className="w-5 h-5 rounded" />
                    <SkeletonLoader type="custom" className="w-5 h-5 rounded" />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <SkeletonLoader
                      type="custom"
                      className="h-8 w-12 mx-auto mb-1"
                    />
                    <SkeletonLoader
                      type="custom"
                      className="h-3 w-16 mx-auto"
                    />
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <SkeletonLoader
                      type="custom"
                      className="h-8 w-12 mx-auto mb-1"
                    />
                    <SkeletonLoader
                      type="custom"
                      className="h-3 w-16 mx-auto"
                    />
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <SkeletonLoader
                      type="custom"
                      className="h-8 w-16 mx-auto mb-1"
                    />
                    <SkeletonLoader
                      type="custom"
                      className="h-3 w-12 mx-auto"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t">
                  <SkeletonLoader type="text" className="h-3 w-32" />
                  <SkeletonLoader type="button" className="h-8 w-20" />
                </div>
              </div>
            ))
          : stores.map((store) => (
              <div
                key={store.id}
                className="card hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3 flex-grow min-w-0 overflow-hidden">
                    <div className="w-12 h-12 bg-gradient-to-r from-brand-indigo-500 to-brand-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FolderOpen className="w-6 h-6 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3
                        className="text-lg font-semibold text-gray-900 truncate"
                        title={store.name}
                      >
                        {store.name}
                      </h3>
                      <p
                        className="text-sm text-gray-600 mt-1 line-clamp-2 break-words"
                        title={store.description}
                      >
                        {store.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    {store?.status !== 'NONE' && (
                      <StatusIndicator
                        status={store.status}
                        showPulse={store.status === 'IN_PROGRESS'}
                      />
                    )}

                    <TooltipArrow content={!canWrite ? 'You do not have permission' : 'Edit'}>
                      <button
                        className={`text-brand-indigo-500 hover:text-brand-indigo-700${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                        disabled={!canWrite}
                        onClick={() => {
                          if (!canWrite) return;
                          setSelectedStore(store);
                          setShowUpdateModal(true);
                        }}
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                    </TooltipArrow>

                    <TooltipArrow content={!canWrite ? 'You do not have permission' : 'Delete'}>
                      <button
                        className={`text-red-400 hover:text-red-600${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                        disabled={!canWrite}
                        onClick={() => {
                          if (!canWrite) return;
                          setStoreToDelete({ id: store.id, name: store.name });
                          setSelectedStore(store);
                          setShowDeleteModal(true);
                        }}
                      >
                        <Trash className="w-5 h-5" />
                      </button>
                    </TooltipArrow>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {store.documentsCount}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">Documents</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {store.aiProcessedCount}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">Processed</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      {store.totalSize}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">Size</p>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="text-xs text-gray-500">
                    <p className="truncate flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>{store.lastActivity}</span>
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      navigate(`/admin/knowledge/documents/${store.id}`)
                    }
                    className={`flex items-center gap-1 text-sm font-medium text-brand-indigo-600 hover:text-brand-indigo-700 whitespace-nowrap`}
                  >
                    View <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
      </div>

      {/* Empty State */}
      {stores.length === 0 && !isLoadingStores && (
        <div className="text-center py-12">
          <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">No document stores found</p>
          <p className="text-sm text-gray-500">
            Create your first store to start managing documents
          </p>
        </div>
      )}

      {/* Pagination */}
      {!isLoadingStores && totalRecords > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalRecords}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(newItemsPerPage) => {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1); // Reset to first page when changing items per page
          }}
          showItemsPerPage={true}
          itemsPerPageOptions={[6, 12, 24, 48]}
        />
      )}

      {/* Update Modal */}
      {showUpdateModal && selectedStore && (
        <UpdateDocumentModal
          isOpen={showUpdateModal}
          onClose={() => setShowUpdateModal(false)}
          document={selectedStore}
          onSubmit={(data: any) => {
            updateStoreMutation.mutate({ id: selectedStore.id, data });
          }}
        />
      )}
      {/* Create Store Modal */}
      {showCreateStoreModal && (
        <CreateDocumentModal
          isOpen={showCreateStoreModal}
          onClose={() => setShowCreateStoreModal(false)}
          onSubmit={(data: any) => createStoreMutation.mutate(data)}
          onDelete={() => {}}
          document={null}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedStore(null);
          setStoreToDelete(null);
        }}
        onConfirm={handleDeleteStore}
        title="Delete Document Store"
        description="Are you sure you want to delete this document store? This action cannot be undone."
        itemName={storeToDelete?.name}
        isLoading={loading}
      />
    </div>
  );
}
